import { useCallback } from 'react';
import { createAuditLog, CreateAuditLogParams } from '../utils/auditLogger';
import { AuditLogCategory, AuditLogAction } from '../types/auditLog';

/**
 * Audit log oluşturmak için hook
 * 
 * @example
 * const { logAction } = useAuditLog();
 * 
 * // Tedarik oluşturma
 * logAction.supplyCreated(supply.id, supply.name);
 * 
 * // Kullanıcı güncelleme
 * logAction.userUpdated(user.id, user.name, changes);
 */
export function useAuditLog() {
  const log = useCallback((params: CreateAuditLogParams) => {
    return createAuditLog(params);
  }, []);

  // Önceden tanımlı log fonksiyonları
  const logAction = {
    // AUTH
    login: (userName: string) => log({
      category: 'AUTH',
      action: 'LOGIN',
      entity: 'User',
      description: `${userName} sisteme giriş yaptı`,
      severity: 'info',
    }),

    logout: (userName: string) => log({
      category: 'AUTH',
      action: 'LOGOUT',
      entity: 'User',
      description: `${userName} sistemden çıkış yaptı`,
      severity: 'info',
    }),

    // USER
    userCreated: (userId: string, userName: string) => log({
      category: 'USER',
      action: 'CREATE',
      entity: 'User',
      entityId: userId,
      entityName: userName,
      description: `Yeni kullanıcı oluşturuldu: ${userName}`,
      severity: 'info',
    }),

    userUpdated: (userId: string, userName: string, changes?: CreateAuditLogParams['changes']) => log({
      category: 'USER',
      action: 'UPDATE',
      entity: 'User',
      entityId: userId,
      entityName: userName,
      description: `Kullanıcı güncellendi: ${userName}`,
      changes,
      severity: 'info',
    }),

    userDeleted: (userId: string, userName: string) => log({
      category: 'USER',
      action: 'DELETE',
      entity: 'User',
      entityId: userId,
      entityName: userName,
      description: `Kullanıcı silindi: ${userName}`,
      severity: 'warning',
    }),

    // ROLE
    roleCreated: (roleId: string, roleName: string) => log({
      category: 'ROLE',
      action: 'CREATE',
      entity: 'Role',
      entityId: roleId,
      entityName: roleName,
      description: `Yeni rol oluşturuldu: ${roleName}`,
      severity: 'info',
    }),

    roleUpdated: (roleId: string, roleName: string, changes?: CreateAuditLogParams['changes']) => log({
      category: 'ROLE',
      action: 'UPDATE',
      entity: 'Role',
      entityId: roleId,
      entityName: roleName,
      description: `Rol güncellendi: ${roleName}`,
      changes,
      severity: 'warning',
    }),

    roleDeleted: (roleId: string, roleName: string) => log({
      category: 'ROLE',
      action: 'DELETE',
      entity: 'Role',
      entityId: roleId,
      entityName: roleName,
      description: `Rol silindi: ${roleName}`,
      severity: 'warning',
    }),

    permissionChanged: (roleId: string, roleName: string, module: string, actions: string[]) => log({
      category: 'ROLE',
      action: 'PERMISSION_CHANGE',
      entity: 'Role',
      entityId: roleId,
      entityName: roleName,
      description: `${roleName} rolünün ${module} modülü için yetkileri değiştirildi`,
      metadata: { module, actions },
      severity: 'warning',
    }),

    // SUPPLY
    supplyCreated: (supplyId: string, supplyName: string) => log({
      category: 'SUPPLY',
      action: 'CREATE',
      entity: 'Supply',
      entityId: supplyId,
      entityName: supplyName,
      description: `Yeni tedarik oluşturuldu: ${supplyName}`,
      severity: 'info',
    }),

    supplyUpdated: (supplyId: string, supplyName: string, changes?: CreateAuditLogParams['changes']) => log({
      category: 'SUPPLY',
      action: 'UPDATE',
      entity: 'Supply',
      entityId: supplyId,
      entityName: supplyName,
      description: `Tedarik güncellendi: ${supplyName}`,
      changes,
      severity: 'info',
    }),

    supplyDeleted: (supplyId: string, supplyName: string) => log({
      category: 'SUPPLY',
      action: 'DELETE',
      entity: 'Supply',
      entityId: supplyId,
      entityName: supplyName,
      description: `Tedarik silindi: ${supplyName}`,
      severity: 'warning',
    }),

    // VEHICLE
    vehicleCreated: (vehicleId: string, vehiclePlate: string) => log({
      category: 'VEHICLE',
      action: 'CREATE',
      entity: 'Vehicle',
      entityId: vehicleId,
      entityName: vehiclePlate,
      description: `Yeni araç eklendi: ${vehiclePlate}`,
      severity: 'info',
    }),

    vehicleUpdated: (vehicleId: string, vehiclePlate: string, changes?: CreateAuditLogParams['changes']) => log({
      category: 'VEHICLE',
      action: 'UPDATE',
      entity: 'Vehicle',
      entityId: vehicleId,
      entityName: vehiclePlate,
      description: `Araç güncellendi: ${vehiclePlate}`,
      changes,
      severity: 'info',
    }),

    vehicleDeleted: (vehicleId: string, vehiclePlate: string) => log({
      category: 'VEHICLE',
      action: 'DELETE',
      entity: 'Vehicle',
      entityId: vehicleId,
      entityName: vehiclePlate,
      description: `Araç silindi: ${vehiclePlate}`,
      severity: 'warning',
    }),

    vehicleAssigned: (vehicleId: string, vehiclePlate: string, driverName: string) => log({
      category: 'VEHICLE',
      action: 'ASSIGN',
      entity: 'Vehicle',
      entityId: vehicleId,
      entityName: vehiclePlate,
      description: `${vehiclePlate} aracı ${driverName} sürücüsüne atandı`,
      metadata: { driverName },
      severity: 'info',
    }),

    // SHIPMENT
    shipmentCreated: (shipmentId: string, shipmentName: string) => log({
      category: 'SHIPMENT',
      action: 'CREATE',
      entity: 'Shipment',
      entityId: shipmentId,
      entityName: shipmentName,
      description: `Yeni sevkiyat oluşturuldu: ${shipmentName}`,
      severity: 'info',
    }),

    shipmentStatusChanged: (shipmentId: string, shipmentName: string, oldStatus: string, newStatus: string) => log({
      category: 'SHIPMENT',
      action: 'STATUS_CHANGE',
      entity: 'Shipment',
      entityId: shipmentId,
      entityName: shipmentName,
      description: `Sevkiyat durumu değişti: ${oldStatus} → ${newStatus}`,
      changes: [{ field: 'status', oldValue: oldStatus, newValue: newStatus }],
      severity: 'info',
    }),

    // BID
    bidCreated: (bidId: string, offerName: string, amount: number) => log({
      category: 'BID',
      action: 'CREATE',
      entity: 'Bid',
      entityId: bidId,
      entityName: offerName,
      description: `Yeni teklif verildi: ${offerName} - ₺${amount.toLocaleString('tr-TR')}`,
      metadata: { amount },
      severity: 'info',
    }),

    bidAccepted: (bidId: string, offerName: string, driverName: string, amount: number) => log({
      category: 'BID',
      action: 'APPROVE',
      entity: 'Bid',
      entityId: bidId,
      entityName: offerName,
      description: `Teklif kabul edildi: ${driverName} - ₺${amount.toLocaleString('tr-TR')}`,
      metadata: { driverName, amount },
      severity: 'info',
    }),

    bidRejected: (bidId: string, offerName: string, driverName: string) => log({
      category: 'BID',
      action: 'REJECT',
      entity: 'Bid',
      entityId: bidId,
      entityName: offerName,
      description: `Teklif reddedildi: ${driverName}`,
      metadata: { driverName },
      severity: 'info',
    }),

    // OFFER
    offerRequestCreated: (offerId: string, offerName: string) => log({
      category: 'OFFER',
      action: 'CREATE',
      entity: 'OfferRequest',
      entityId: offerId,
      entityName: offerName,
      description: `Yeni teklif talebi oluşturuldu: ${offerName}`,
      severity: 'info',
    }),

    offerRequestStatusChanged: (offerId: string, offerName: string, oldStatus: string, newStatus: string) => log({
      category: 'OFFER',
      action: 'STATUS_CHANGE',
      entity: 'OfferRequest',
      entityId: offerId,
      entityName: offerName,
      description: `Teklif talebi durumu değişti: ${oldStatus} → ${newStatus}`,
      changes: [{ field: 'status', oldValue: oldStatus, newValue: newStatus }],
      severity: 'info',
    }),

    // EXPENSE
    expenseSubmitted: (expenseId: string, description: string, amount: number) => log({
      category: 'EXPENSE',
      action: 'SUBMIT',
      entity: 'Expense',
      entityId: expenseId,
      entityName: description,
      description: `Masraf gönderildi: ${description} - ₺${amount.toLocaleString('tr-TR')}`,
      metadata: { amount },
      severity: 'info',
    }),

    expenseApproved: (expenseId: string, description: string, amount: number) => log({
      category: 'EXPENSE',
      action: 'APPROVE',
      entity: 'Expense',
      entityId: expenseId,
      entityName: description,
      description: `Masraf onaylandı: ${description} - ₺${amount.toLocaleString('tr-TR')}`,
      metadata: { amount },
      severity: 'info',
    }),

    expenseRejected: (expenseId: string, description: string, reason: string) => log({
      category: 'EXPENSE',
      action: 'REJECT',
      entity: 'Expense',
      entityId: expenseId,
      entityName: description,
      description: `Masraf reddedildi: ${description} - Sebep: ${reason}`,
      metadata: { reason },
      severity: 'warning',
    }),

    // DOCUMENT
    documentUploaded: (docId: string, docName: string, docType: string) => log({
      category: 'DOCUMENT',
      action: 'UPLOAD',
      entity: 'Document',
      entityId: docId,
      entityName: docName,
      description: `Belge yüklendi: ${docName} (${docType})`,
      metadata: { docType },
      severity: 'info',
    }),

    documentDeleted: (docId: string, docName: string) => log({
      category: 'DOCUMENT',
      action: 'DELETE',
      entity: 'Document',
      entityId: docId,
      entityName: docName,
      description: `Belge silindi: ${docName}`,
      severity: 'warning',
    }),

    // WAREHOUSE
    warehouseTransfer: (fromWarehouse: string, toWarehouse: string, itemCount: number) => log({
      category: 'WAREHOUSE',
      action: 'UPDATE',
      entity: 'WarehouseTransfer',
      description: `Depo transferi: ${fromWarehouse} → ${toWarehouse} (${itemCount} ürün)`,
      metadata: { fromWarehouse, toWarehouse, itemCount },
      severity: 'info',
    }),

    rampAssigned: (shipmentId: string, rampName: string) => log({
      category: 'WAREHOUSE',
      action: 'ASSIGN',
      entity: 'Ramp',
      entityId: shipmentId,
      entityName: rampName,
      description: `Rampa atandı: ${rampName}`,
      metadata: { rampName },
      severity: 'info',
    }),

    // SETTINGS
    settingsUpdated: (settingName: string, oldValue: any, newValue: any) => log({
      category: 'SETTINGS',
      action: 'UPDATE',
      entity: 'Settings',
      entityName: settingName,
      description: `Ayar güncellendi: ${settingName}`,
      changes: [{ field: settingName, oldValue, newValue }],
      severity: 'warning',
    }),

    // Özel log
    custom: (params: CreateAuditLogParams) => log(params),
  };

  return { log, logAction };
}
